/** Application logs. They carry no identifiers, by construction. */

import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Button,
  Dropdown,
  Input,
  Option,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { PlayRegular, StopRegular } from '@fluentui/react-icons'
import {
  EmptyState,
  FilterBar,
  LoadingState,
  TerminalBody,
  TerminalHeader,
  TerminalLine,
  TerminalStatusBar,
  TerminalSurface,
  formatTimestamp,
} from '../platform'
import type { TerminalLineTone } from '../platform'
import { api } from '../api'
import type { LogLine, Page } from '../api'

const LEVELS = ['everything', 'debug', 'info', 'warning', 'error'] as const

/** How often a followed page asks for more. */
const FOLLOW_MS = 3000

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    height: '100%',
    minHeight: 0,
  },
  surface: {
    flex: 1,
    minHeight: '320px',
  },
  correlation: {
    width: '320px',
    maxWidth: '100%',
    minWidth: 0,
    '@media (max-width: 719px)': {
      width: 'calc(100vw - 80px)',
      maxWidth: 'none',
    },
  },
  level: {
    minWidth: '160px',
  },
})

export default function Logs() {
  const styles = useStyles()
  const [params, setParams] = useSearchParams()
  const [lines, setLines] = useState<LogLine[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [follow, setFollow] = useState(false)
  const body = useRef<HTMLDivElement>(null)

  const level = params.get('level') ?? ''
  const correlation = params.get('correlation_id') ?? ''

  useEffect(() => {
    const load = () => {
      const query = new URLSearchParams()
      if (level) query.set('level', level)
      if (correlation) query.set('correlation_id', correlation)
      api
        .get<Page<LogLine>>(`/logs?${query}`)
        .then((p) => setLines(p.items))
        .catch((e) => setError(e.message))
    }
    load()
    if (!follow) return
    const timer = setInterval(load, FOLLOW_MS)
    return () => clearInterval(timer)
  }, [level, correlation, follow])

  useEffect(() => {
    // A followed log that does not move is a log nobody is reading.
    if (follow && body.current) body.current.scrollTop = body.current.scrollHeight
  }, [lines, follow])

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next)
  }

  if (error) return <EmptyState title="The logs cannot be read" description={error} />

  return (
    <div className={styles.page}>
      <FilterBar
        search={
          <Input
            className={styles.correlation}
            value={correlation}
            onChange={(_, d) => set('correlation_id', d.value)}
            placeholder="Paste a correlation id from a decision to see only its lines"
            aria-label="Correlation id"
          />
        }
        filters={
          <Dropdown
            className={styles.level}
            value={level || 'everything'}
            selectedOptions={[level || 'everything']}
            onOptionSelect={(_, d) =>
              set('level', d.optionValue === 'everything' ? '' : (d.optionValue ?? ''))
            }
            aria-label="Level and worse"
          >
            {LEVELS.map((l) => (
              <Option key={l} value={l}>
                {l}
              </Option>
            ))}
          </Dropdown>
        }
        actions={
          <Button
            icon={follow ? <StopRegular /> : <PlayRegular />}
            appearance={follow ? 'primary' : 'secondary'}
            onClick={() => setFollow(!follow)}
          >
            {follow ? 'Stop following' : 'Follow'}
          </Button>
        }
      />

      <TerminalSurface className={styles.surface}>
        <TerminalHeader>
          The last few thousand lines, held in memory. The machine&apos;s journal keeps the durable
          copy. No identifier appears here in any form.
        </TerminalHeader>
        <TerminalBody scrollRef={body}>
          {!lines ? (
            <LoadingState label="Reading the logs" />
          ) : lines.length === 0 ? (
            <EmptyState
              title="No lines to show"
              description="Nothing has been logged at this level in this window."
            />
          ) : (
            lines.map((line, i) => (
              <TerminalLine key={`${line.occurred_at}-${i}`} tone={toneFor(line.level)}>
                {formatTimestamp(line.occurred_at)} {line.level.toUpperCase()} {line.logger}{' '}
                {line.message}
              </TerminalLine>
            ))
          )}
        </TerminalBody>
        <TerminalStatusBar>
          {lines?.length ?? 0} lines, {level || 'every level'}
          {correlation ? `, one authorization` : ''}
          {follow ? `, following every ${FOLLOW_MS / 1000} s` : ''}
        </TerminalStatusBar>
      </TerminalSurface>
    </div>
  )
}

function toneFor(level: string): TerminalLineTone {
  if (level === 'error' || level === 'critical') return 'error'
  if (level === 'warning') return 'command'
  if (level === 'debug') return 'system'
  return 'info'
}
